import moment from 'moment';
import debounce from 'lodash/debounce';
import { mapGetters, mapMutations } from 'vuex';
import * as R from 'ramda';

import RingLoader from '@/components/Common/RingLoader';
import SearchInput from '@/components/Common/searchInput/SearchInput';

import TrashIcon from '@svgIcons/trash.svg';
import EditIcon from '@svgIcons/edit.svg';
import DuplicateIcon from '@svgIcons/duplicate.svg';
import RefreshIcon from '@svgIcons/refresh.svg';
import ApiCenterIcon from '@svgIcons/api_center.svg';

import channelIconMixin from '@/mixins/channelIcons.js';
import deleteDialog from '@/mixins/deleteDialog.js';

import * as S from './style';

import { ExportDataService } from '@/services';

import { setMomentLocale } from '@/utils/timeUtils';
import { isValueExist, looseSearch } from '@/utils/utils';

import HistoryIcon from '@svgIcons/ds_history.svg';
import { TRANSFER_TYPES } from '@/utils/consts';
import { refineChannel } from '@/utils/dashboard';
import withHMR from '@/utils/withHMR';

const { MySQL, Postgres, S3, GoogleSheet, BigQuery, Snowflake, API } =
    TRANSFER_TYPES;

export const DS_DEST_ICONS_MAP = {
    [MySQL]: 'https://app.adriel.com/public/icon_mysql.svg',
    [Postgres]: 'https://app.adriel.com/public/icon_postgres.svg',
    [GoogleSheet]: 'https://app.adriel.com/public/icon_googlespreadsheet.svg',
    [S3]: 'https://app.adriel.com/public/icon_amazonS3.svg',
    [BigQuery]: 'https://app.adriel.com/public/icon_bigQuery.svg',
    [Snowflake]:
        'https://connectors.airbyte.com/files/metadata/airbyte/destination-snowflake/latest/icon.svg',
    [BigQuery]: 'https://app.adriel.com/public/icon_bigQuery.svg',
    [API]: 'https://app.adriel.com/public/icon_api.svg',
};

const PAGE_SIZE_OPTIONS = ['5', '10', '25', '50', '100', '200'];

const checkIsApi = R.pathEq(
    ['dataTransferConfig', 'destination_connector_type'],
    API
);

const ExportDataTable = {
    name: 'ExportDataTable',
    mixins: [channelIconMixin, deleteDialog],
    props: {
        transferList: {
            type: Array,
            default: R.always([]),
            required: true,
        },
        adsTableSources: {
            type: Array,
            default: R.always([]),
        },
        transferOptions: {
            type: Array,
            default: R.always([]),
        },
        isLookerStudio: {
            type: Boolean,
            default: false,
        },
        tableAdAccounts: {
            type: Array,
            default() {
                return [];
            },
        },
    },
    watch: {
        language: {
            handler(value) {
                setMomentLocale(value);
            },
            immediate: true,
        },
        searchQuery: {
            handler(value) {
                // console.log('searchQuery changed:', value);
            },
            immediate: true,
        },
    },
    data() {
        return {
            tableLoading: false,
            searchQuery: undefined,
            // What the field shows; `searchQuery` trails it by the debounce.
            searchInput: '',
            debouncedSearch: null,
        };
    },
    created() {
        this.debouncedSearch = debounce((value) => {
            this.searchQuery = value;
        }, 300);

        // to force triggering rerender for ant-design initial render issue
        setTimeout(() => {
            this.searchQuery = '';
        }, 100);
    },
    destroyed() {
        this.debouncedSearch.cancel();
    },
    computed: {
        ...mapGetters('user', ['pricePlanSettings']),
        adsTableSourcesByAssetId: R.compose(
            R.indexBy(R.prop('assetId')),
            R.propOr([], 'tableAdAccounts')
        ),
        filteredTransferList() {
            if (!this.searchQuery) {
                return this.transferList;
            }

            return looseSearch(
                this.transferList,
                R.pathOr('', ['dataTransferConfig', 'custom_name']),
                this.searchQuery
            );
        },
        tableProps() {
            return {
                attrs: {
                    scroll: this.isMobileSize ? { x: 1000 } : {},
                    tableLayout: 'auto',
                    locale: {
                        emptyText: this.$t('PAYMENT.invoice_table_no_data'),
                    },
                    loading: {
                        indicator: <RingLoader />,
                        spinning: this.tableLoading,
                    },
                    size: 'middle',
                    columns: this.mainHeaders,
                    'data-source': this.filteredTransferList,
                    rowKey: 'id',
                    pagination: {
                        position: 'bottom',
                        showSizeChanger: true,
                        pageSizeOptions: PAGE_SIZE_OPTIONS,
                        locale: {
                            items_per_page: '',
                        },
                    },
                },
            };
        },
        mainHeaders() {
            return [
                {
                    title: 'No.',
                    key: 'number',
                    customRender: (_, _2, index) => {
                        return index + 1;
                    },
                },
                {
                    title: this.$t('EXPORT_DATA.form_label_name'),
                    key: 'name',
                    customRender: ({
                        dataTransferConfig: { custom_name } = {},
                    }) => {
                        return this.wrapTooltip({
                            content: (
                                <S.NameWrapper>
                                    <div
                                        class="adriel-ellipsis"
                                        style={{
                                            width: '100%',
                                        }}
                                    >
                                        {custom_name}
                                    </div>
                                </S.NameWrapper>
                            ),
                            title: custom_name,
                            visible: true,
                            placement: 'top',
                            trigger: 'hover',
                        });
                    },
                },
                {
                    title: this.$t('EXPORT_DATA.form_label_data_source'),
                    key: 'data_source',
                    customRender: ({
                        dataTransferConfig: { asset_id_list = [] } = {},
                    }) => {
                        const uniqChannels = R.pipe(
                            R.map(
                                (v) => this.adsTableSourcesByAssetId[v] || {}
                            ),
                            R.map(refineChannel),
                            R.filter(R.identity),
                            R.uniq
                        )(asset_id_list);

                        return (
                            <S.ChnnelIconsWrapper>
                                {this.channelIconsFromArray(uniqChannels)}
                            </S.ChnnelIconsWrapper>
                        );
                    },
                },
                {
                    title: this.$t('EXPORT_DATA.form_label_destination'),
                    key: 'destination',
                    customRender: ({
                        dataTransferConfig: {
                            destination_connector_type,
                            destination_table_name,
                        } = {},
                    }) => {
                        let icon =
                            DS_DEST_ICONS_MAP[destination_connector_type];

                        return (
                            <S.DestinationWrapper>
                                {typeof icon === 'string' ? (
                                    <img
                                        width="30px"
                                        height="30px"
                                        src={icon}
                                    />
                                ) : (
                                    icon && <icon />
                                )}
                                {destination_connector_type !== API
                                    ? this.wrapTooltip({
                                          content: (
                                              <div
                                                  class="adriel-ellipsis"
                                                  style={{
                                                      width: '100%',
                                                  }}
                                              >
                                                  {destination_table_name}
                                              </div>
                                          ),
                                          title: destination_table_name,
                                          visible: true,
                                          placement: 'top',
                                          trigger: 'hover',
                                      })
                                    : 'API'}
                            </S.DestinationWrapper>
                        );
                    },
                },
                {
                    title: this.$t('EXPORT_DATA.form_label_updated_at'),
                    key: 'last_update',
                    customRender: (data) => {
                        // {
                        //     status: 'SUCCEEDED',
                        //     createdAt: new Date(),
                        //     endedAt: new Date(),
                        //     durationInSeconds: 0
                        // }
                        const { transferStatus = [] } = data;
                        const succeededJob = transferStatus[0];
                        const date = moment(succeededJob?.endedAt);

                        const $history = (
                            <HistoryIcon
                                onClick={() =>
                                    this.$emit('setHistoryTarget', data)
                                }
                            />
                        );

                        const $date = isValueExist(succeededJob)
                            ? date.format(this.$t('COMMON.date_time_format'))
                            : '-';

                        return (
                            <S.UpdatedWrapper>
                                {$date}
                                {$history}
                            </S.UpdatedWrapper>
                        );
                    },
                },
                {
                    title: ' ',
                    customRender: (item) => {
                        const isApi = checkIsApi(item);

                        return (
                            <S.EditTrashIconWrapper>
                                <EditIcon
                                    v-view-box
                                    class="edit-icon"
                                    onClick={() => this.onClickEdit(item)}
                                />
                                <TrashIcon
                                    v-view-box
                                    class="trash-icon"
                                    onClick={() =>
                                        this.onClickDeleteTransfer(item)
                                    }
                                />
                                <DuplicateIcon
                                    v-view-box
                                    class="duplicate-icon"
                                    onClick={() => this.onClickDuplicate(item)}
                                />
                                {!isApi && (
                                    <RefreshIcon
                                        v-view-box
                                        class="refresh-icon"
                                        onClick={() =>
                                            this.onClickManualTrigger(item)
                                        }
                                    />
                                )}
                            </S.EditTrashIconWrapper>
                        );
                    },
                },
            ];
        },
    },
    methods: {
        ...mapMutations('view', ['setNotiMsgs']),
        onClickDeleteTransfer({
            dataTransferConfig: {
                custom_name = '',
                destination_table_name = '',
            } = {},
        }) {
            return this.confirmDelete({
                value: custom_name,
                message: this.$t('EXPORT_DATA.delete_message'),
                cb: async () => {
                    try {
                        this.tableLoading = true;

                        const param = {
                            dataTransferName: custom_name,
                            ...(destination_table_name && {
                                destinationTableName: destination_table_name,
                            }),
                        };

                        await ExportDataService.deleteTransferV2(param);
                        await this.$emit('fetchList');
                    } catch (e) {
                        console.error(e);
                    } finally {
                        this.tableLoading = false;
                    }
                },
            });
        },
        onClickEdit(item) {
            this.$emit('setSelectedItem', item);
        },
        onClickDuplicate(item) {
            this.$emit('setSelectedItem', R.assoc('isDuplicate', true, item));
        },
        async onClickManualTrigger({ dataTransferConfig: { id } = {} }) {
            const param = {
                dataTransferSettingId: id,
            };
            try {
                await ExportDataService.triggerManualTransfer(param);
                this.setNotiMsgs([
                    {
                        type: 'SUCCESS',
                        title: this.$t('EXPORT_DATA.datastream_triggered'),
                        reasons: [{}],
                    },
                ]);
            } catch (err) {
                const { code = '' } = R.pathOr({}, ['response', 'data'], err);

                const reason = this.$t(`EXPORT_DATA.error_${code}`);

                this.setNotiMsgs([{ type: 'FAIL', reasons: [reason] }]);
            }
        },
        renderTable() {
            return <S.ATable {...this.tableProps}></S.ATable>;
        },
    },
    render() {
        const allowDataTransferAPI =
            this.pricePlanSettings?.allowDataTransferAPI;

        return (
            <S.TableWrapper locale={this.$i18n.locale}>
                <S.SearchInputWrapper>
                    {allowDataTransferAPI && (
                        <S.GotoApiCenterButton
                            type="secondary"
                            onClick={() => {
                                this.$router.push('/dataTransfer/apiCenter');
                            }}
                        >
                            <ApiCenterIcon />
                            <span>{this.$t('API_CENTER.title')}</span>
                        </S.GotoApiCenterButton>
                    )}
                    <S.SearchInputContainer>
                        <SearchInput
                            value={this.searchInput}
                            placeholder={this.translateOr(
                                'EXPORT_DATA.table_search_placeholder',
                                'Search name'
                            )}
                            onInput={(value) => {
                                this.searchInput = value;
                                if (value === '') {
                                    // Clear applies at once, not after the debounce.
                                    this.debouncedSearch.cancel();
                                    this.searchQuery = '';
                                    return;
                                }
                                this.debouncedSearch(value);
                            }}
                        />
                    </S.SearchInputContainer>
                </S.SearchInputWrapper>
                {this.renderTable()}
            </S.TableWrapper>
        );
    },
};

export default withHMR(module, ExportDataTable, ExportDataTable.name);
